import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-use';

import { Table, TableCell, TableHead, TableHeadCell, TableRow } from '@ifs-gcp/components';
import type { ITemplateNode } from '@ifs-gcp/core';
import type { AuditResultWidgetChild, ResultLevel } from '@ifs-gcp/core/models';

import { loadJsonChecklist } from '@/hooks/useJsonTemplate';
import { useAuditStore } from '@/store/auditStore';
import { getTranslation } from '@/utils/translations';

import styles from './result.module.scss';

interface WidgetAuditResultProps {
  element: ITemplateNode<AuditResultWidgetChild>;
}

export const WidgetAuditResult = observer(
  ({
    element: {
      props: { variant, noScoring, showPoints, context: widgetContext, checklistId, translations },
    },
  }: WidgetAuditResultProps) => {
    const {
      i18n: { language },
    } = useTranslation();

    const audit = useAuditStore((state) => state.audit)!;

    const moduleId = widgetContext[0];

    const checklistDefinition = useAsync(async () => {
      const result = await loadJsonChecklist(checklistId);

      if (!result) {
        throw new Error(
          `Error while loading checklist ${checklistId} for widget audit result in ${moduleId}`,
        );
      }

      return result;
    });

    const { basicAuditResults } = audit.standards[moduleId];

    if (checklistDefinition.loading) {
      return null;
    }

    if (checklistDefinition.error || !checklistDefinition.value) {
      throw new Error(checklistDefinition?.error?.message);
    }

    const module = audit.modules.getById(moduleId);

    if (!module?.result) {
      return null;
    }

    const { levelsSetting } = checklistDefinition.value;

    const {
      result: {
        overall: { id: overallId, points: achievedPoints, maximumPoints: maxPoints, ...overall },
        levels,
      },
    } = module;

    if (!basicAuditResults[overallId]) {
      throw new Error('Calculation result is not included in the possible results of the module');
    }

    const { name, qualifiedForFollowUp, followUpSuffix, colorCode } = basicAuditResults[overallId];

    const auditResult = getTranslation(name, language);
    const resultSuffix =
      qualifiedForFollowUp && followUpSuffix ? ` ${getTranslation(followUpSuffix, language)}` : '';
    const pointsContent = `${achievedPoints} / ${maxPoints} ${getTranslation(translations.points, language)}`;

    return (
      <Table
        head={
          <TableHead>
            {!noScoring && (
              <TableHeadCell width={140}>
                {getTranslation(translations.scoring, language)}
              </TableHeadCell>
            )}
            <TableHeadCell>{getTranslation(translations.result, language)}</TableHeadCell>
          </TableHead>
        }
      >
        <ResultRow
          {...overall}
          noScoring={noScoring}
          showPoints={showPoints}
          content={`${auditResult}${resultSuffix}`}
          pointsContent={pointsContent}
          colorCode={colorCode}
        />

        {variant === 'level' && (
          <>
            <TableRow>
              <TableCell className={styles.levels}>
                {getTranslation(translations.level, language)}
              </TableCell>
            </TableRow>

            {levels.map(({ id: levelResultId, level, ...params }) => {
              const {
                name: levelName,
                qualifiedForFollowUp: levelQualifiedForFollowUp,
                followUpSuffix: levelFollowUpSuffix,
              } = basicAuditResults[levelResultId];

              const levelText = getTranslation(levelsSetting[level].text, language);

              const levelResult = getTranslation(levelName, language);

              const levelResultSuffix =
                levelQualifiedForFollowUp && levelFollowUpSuffix
                  ? ` ${getTranslation(levelFollowUpSuffix, language)}`
                  : '';

              return (
                <ResultRow
                  key={level}
                  {...params}
                  level={level}
                  noScoring={noScoring}
                  showPoints={showPoints}
                  colorCode={colorCode}
                  content={`${levelText} - ${levelResult}${levelResultSuffix}`}
                />
              );
            })}
          </>
        )}
      </Table>
    );
  },
);

const formatPercentage = (value: number) => {
  return `${(Math.round(value * 100) / 100).toFixed(2).replace('.', ',')} %`;
};

type ResultRowProps = Omit<ResultLevel, 'id'> &
  Pick<AuditResultWidgetChild['props'], 'noScoring' | 'showPoints'> & {
    content: string;
    pointsContent?: string;
    colorCode?: string;
  };

const ResultRow: React.FC<ResultRowProps> = ({
  percent,
  noScoring,
  showPoints,
  content,
  pointsContent,
  colorCode,
}) => {
  return (
    <TableRow>
      {!noScoring && (
        <TableCell>
          <span
            className={styles.status}
            style={{
              color: colorCode ?? 'black',
            }}
          >
            {formatPercentage(percent)}
          </span>
          {showPoints && (
            <span
              style={{
                color: colorCode ?? 'black',
              }}
              className={styles.status}
            >
              {pointsContent ?? ''}
            </span>
          )}
        </TableCell>
      )}

      <TableCell>{content}</TableCell>
    </TableRow>
  );
};
